import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { IncomeStatusChip, fmtDate, getErrorMessage, unwrap } from './IncomeShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'COMPLETED', 'VOID'];

const emptyForm = () => ({
  description: '', total_amount: '', start_date: new Date().toISOString().slice(0, 10), end_date: '',
  revenue_account_id: '', deferred_revenue_account_id: '',
});

// Deferred revenue schedule: a lump sum billed/collected once, recognised
// into income over a period range. Posting the schedule generates the
// individual per-period recognitions (see RevenueRecognitionsTab.js).
// DRAFT -> APPROVED -> POSTED; approve/void run as checker, create/post as
// maker - see receivables/InvoicesTab.js for the same verified lifecycle
// this follows (no direct ground truth exists for this endpoint itself).
const RevenueSchedulesTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INCOME, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.INCOME, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.INCOME, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!values.description.trim()) {
      showSnackbar('Description is required', 'error');
      return;
    }
    if (!(Number(values.total_amount) > 0)) {
      showSnackbar('Total amount must be greater than zero', 'error');
      return;
    }
    if (!values.end_date) {
      showSnackbar('End date is required', 'error');
      return;
    }
    if (!values.revenue_account_id || !values.deferred_revenue_account_id) {
      showSnackbar('Revenue account and deferred revenue account are required', 'error');
      return;
    }
    const payload = {
      description: values.description.trim(),
      total_amount: Number(values.total_amount),
      start_date: values.start_date,
      end_date: values.end_date,
      revenue_account_id: Number(values.revenue_account_id),
      deferred_revenue_account_id: Number(values.deferred_revenue_account_id),
    };
    try {
      setSaving(true);
      await ledgerAPI.createRevenueSchedule(payload);
      showSnackbar('Revenue schedule created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this revenue schedule?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Revenue schedule ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getRevenueSchedule(row.schedule_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch revenue schedule'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidRevenueSchedule(voidDlg.item.schedule_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Revenue schedule voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailPeriods = Array.isArray(detail?.periods) ? detail.periods : [];

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.INCOME}
        title="Revenue Schedules"
        subtitle="Deferred revenue recognised into income over a period range"
        addLabel="New Schedule"
        idKey="schedule_id"
        searchPlaceholder="Search schedule #, description..."
        emptyMessage="No revenue schedules found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getRevenueSchedules(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Period' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'total' },
        ]}
        columns={[
          { label: 'Schedule #', render: (s) => <strong>{s.schedule_number || `SCH-${s.schedule_id}`}</strong> },
          { label: 'Description', render: (s) => s.description },
          { label: 'Period', render: (s) => `${fmtDate(s.start_date)} - ${fmtDate(s.end_date)}` },
          { label: 'Total', render: (s) => formatAccountingAmount(s.total_amount), align: 'right' },
          { label: 'Recognised', render: (s) => formatAccountingAmount(s.recognized_amount), align: 'right', hideOnMobile: true },
          { label: 'Status', render: (s) => <IncomeStatusChip status={s.status} /> },
        ]}
        rowActions={(s) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(s) }];
          if (s.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveRevenueSchedule(s.schedule_id)) });
          }
          if (s.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postRevenueSchedule(s.schedule_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(s.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: s, void_reason: '' }) });
          }
          if (s.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete revenue schedule',
                message: `Delete DRAFT schedule ${s.schedule_number || `SCH-${s.schedule_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteRevenueSchedule(s.schedule_id);
                    showSnackbar('Revenue schedule deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Revenue Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Description" value={values.description}
                onChange={(e) => setValues((p) => ({ ...p, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="number" label="Total Amount" value={values.total_amount}
                onChange={(e) => setValues((p) => ({ ...p, total_amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="Start Date" value={values.start_date}
                onChange={(e) => setValues((p) => ({ ...p, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="End Date" value={values.end_date}
                onChange={(e) => setValues((p) => ({ ...p, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Revenue account" value={values.revenue_account_id} onChange={(id) => setValues((p) => ({ ...p, revenue_account_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Deferred revenue account" value={values.deferred_revenue_account_id} onChange={(id) => setValues((p) => ({ ...p, deferred_revenue_account_id: id }))} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Schedule {detail?.schedule_number || (detail?.schedule_id ? `SCH-${detail.schedule_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detail.description} - {fmtDate(detail.start_date)} to {fmtDate(detail.end_date)}
              </Typography>
              <Typography variant="body2">Revenue account: {look.accountLabel(detail.revenue_account_id, detail.revenue_account_id)}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>Deferred revenue account: {look.accountLabel(detail.deferred_revenue_account_id, detail.deferred_revenue_account_id)}</Typography>
              {detailPeriods.length > 0 && (
                <DataTable
                  columns={['Period', 'Amount', 'Status']}
                  data={detailPeriods}
                  loading={false}
                  emptyMessage="No periods."
                  renderRow={(p, i) => (
                    <TableRow key={p.recognition_id ?? i}>
                      <TableCell>{fmtDate(p.period_date ?? p.recognition_date)}</TableCell>
                      <TableCell>{formatAccountingAmount(p.amount)}</TableCell>
                      <TableCell><IncomeStatusChip status={p.status} /></TableCell>
                    </TableRow>
                  )}
                />
              )}
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Total: <strong>{formatAccountingAmount(detail.total_amount)}</strong></Typography>
                <Typography variant="body2">Recognised: <strong>{formatAccountingAmount(detail.recognized_amount)}</strong></Typography>
                <IncomeStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void schedule ${voidDlg.item?.schedule_number || (voidDlg.item ? `SCH-${voidDlg.item.schedule_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default RevenueSchedulesTab;
